import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'all', label: 'All' },
  { value: 'campus', label: 'Campus' },
  { value: 'classroom', label: 'Classroom' },
  { value: 'events', label: 'Events' },
  { value: 'results', label: 'Results' },
  { value: 'seminars', label: 'Seminars' },
];

const defaultGallery = [
  { id: '1', title: 'Campus Building', category: 'campus', image_url: 'https://images.unsplash.com/photo-1562774053-701939374585?w=600&q=80' },
  { id: '2', title: 'Modern Classroom', category: 'classroom', image_url: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&q=80' },
  { id: '3', title: 'Annual Day Event', category: 'events', image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80' },
  { id: '4', title: 'Result Celebration', category: 'results', image_url: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&q=80' },
  { id: '5', title: 'Faculty Seminar', category: 'seminars', image_url: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&q=80' },
  { id: '6', title: 'Library', category: 'campus', image_url: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&q=80' },
  { id: '7', title: 'Lab Session', category: 'classroom', image_url: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=600&q=80' },
  { id: '8', title: 'Sports Day', category: 'events', image_url: 'https://images.unsplash.com/photo-1461896836934- voices-logo?w=600&q=80' },
  { id: '9', title: 'Topper Felicitation', category: 'results', image_url: 'https://images.unsplash.com/photo-1559223607-a43c990c692c?w=600&q=80' },
];

const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/gallery`);
        setGallery(response.data.length > 0 ? response.data : defaultGallery);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setGallery(defaultGallery);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const filteredGallery = selectedCategory === 'all'
    ? gallery
    : gallery.filter(item => item.category === selectedCategory);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setSelectedImage(filteredGallery[index]);
  };

  const nextImage = () => {
    const newIndex = (lightboxIndex + 1) % filteredGallery.length;
    setLightboxIndex(newIndex);
    setSelectedImage(filteredGallery[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (lightboxIndex - 1 + filteredGallery.length) % filteredGallery.length;
    setLightboxIndex(newIndex);
    setSelectedImage(filteredGallery[newIndex]);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4">Gallery</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-slate-900 leading-tight">
              A Glimpse Into <span className="text-primary">Life at Caliber Nova</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Explore memorable moments from our classrooms, events, activities, and student achievements. Every picture reflects our passion for learning, growth, and academic excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b sticky top-16 bg-white z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className="h-auto flex-wrap justify-center bg-transparent p-0 gap-2">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  data-testid={`gallery-filter-${cat.value}`}
                  className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-full px-4 py-2"
                >
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="aspect-square bg-slate-200 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : filteredGallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredGallery.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className="aspect-square rounded-xl overflow-hidden bg-slate-100 relative">
                    <img
                      src={item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url.startsWith('/') ? '' : '/'}${item.image_url}`}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&q=80';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-center p-4">
                        <p className="font-medium">{item.title}</p>
                        <Badge variant="secondary" className="mt-2">{item.category}</Badge>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Images Found</h3>
              <p className="text-muted-foreground">No images available in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-0">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-6 h-6" />
            </Button>

            {selectedImage && (
              <div className="relative">
                <img
                  src={selectedImage.image_url.startsWith('http') ? selectedImage.image_url : `${API_URL}${selectedImage.image_url.startsWith('/') ? '' : '/'}${selectedImage.image_url}`}
                  alt={selectedImage.title}
                  className="w-full max-h-[80vh] object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <h3 className="text-white font-heading text-xl">{selectedImage.title}</h3>
                  <Badge variant="secondary" className="mt-2">{selectedImage.category}</Badge>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                  onClick={prevImage}
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default GalleryPage;
